// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import DataTableContext from "@conklin/MobiusOverrides/DataTable/DataTableContext";
import { fontConfig } from "@conklin/XDL/PreStyle/typography";
import { ellipsis } from "@conklin/XDL/Utilities";
import { StyledButton } from "@insite/mobius/Clickable";
import DataTableCellBase, { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import * as React from "react";
import styled, { css, withTheme } from "styled-components";

export interface DataTableCellProps extends DataTableCellBaseProps {
    /** Props to be passed to the cell's typography child. */
    typographyProps?: TypographyPresentationProps;
    /**
     * Indicates how the `css` property is combined with the default `css` property from the theme.
     * If true, the default css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the default css in the theme.
     */
    mergeCss?: boolean;
    // XNG-Changes
    header?: string;
}

// XNG-Changes
const MobileDataTableCellBase = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    column-gap: 12px;

    > ${StyledButton} {
        margin-left: auto;
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;

        ${TypographyStyle} {
            font-size: 14px;
            line-height: 22px;
            font-weight: ${fontConfig.weight.bold};
            text-align: right;
            ${ellipsis(2)}
        }
    }
`;

// XNG-Changes
const HeaderTypography = styled.p`
    flex: 0 0 calc(50% - 8px);
    font-size: 14px;
    line-height: 22px;
    font-weight: ${fontConfig.weight.bold};
    white-space: nowrap;
    text-align: left;

    &:empty {
        display: none;
    }
`;

// XNG-Changes
const ValueTypography = styled.p`
    flex: 0 0 calc(50% - 4px);
    font-size: 14px;
    line-height: 22px;
    text-align: right;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const DataTableCell: React.FC<DataTableCellProps> = ({
    mergeCss,
    css: cellCss = "",
    children,
    typographyProps,
    ...otherProps
}) => {
    const { applyStyledProp } = applyPropBuilder({ ...otherProps }, { component: "dataTable" });
    const { theme } = otherProps;
    const resolvedMergeCss = mergeCss ?? theme?.dataTable.defaultProps?.mergeCss;
    // XNG-Changes: add MobileDataTableCellBase
    return (
        <DataTableContext.Consumer>
            {({ cellTypographyProps, isMobile, useDesktopViewForMobile }) =>
                isMobile && !useDesktopViewForMobile ? (
                    <MobileDataTableCellBase
                        {...otherProps}
                        css={css`
                            ${applyStyledProp(["cssOverrides", "cell"], resolvedMergeCss)}
                            ${cellCss}
                        `}
                    >
                        <HeaderTypography>{otherProps.header && otherProps.header}</HeaderTypography>
                        {typeof children === "string" ? (
                            <ValueTypography {...cellTypographyProps} {...typographyProps}>
                                {children}
                            </ValueTypography>
                        ) : (
                            children
                        )}
                    </MobileDataTableCellBase>
                ) : (
                    <DataTableCellBase
                        {...otherProps}
                        css={css`
                            ${applyStyledProp(["cssOverrides", "cell"], resolvedMergeCss)}
                            ${cellCss}
                        `}
                    >
                        {typeof children === "string" || typeof children === "number" ? (
                            <Typography {...cellTypographyProps} {...typographyProps}>
                                {children}
                            </Typography>
                        ) : (
                            children
                        )}
                    </DataTableCellBase>
                )
            }
        </DataTableContext.Consumer>
    );
};

export default withTheme(DataTableCell);
